import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, combineLatest, map } from 'rxjs';

import { SbvpRole, UserState } from '../store/user/user.state';
import {
  getAllowedApplicationIds,
  getAllowedClientIds,
  getSbvpRole,
  isAdminSbvp,
} from '../store/user/user.selector';

@Injectable({ providedIn: 'root' })
export class SbvpAuthService {
  /** Rôle SBVP dominant de l'utilisateur connecté. */
  readonly sbvpRole$: Observable<SbvpRole | null>;

  /** true si l'utilisateur est admin_sbvp (accès total). */
  readonly isAdminSbvp$: Observable<boolean>;

  constructor(private store: Store<{ user: UserState }>) {
    this.sbvpRole$ = this.store.select(getSbvpRole);
    this.isAdminSbvp$ = this.store.select(isAdminSbvp);
  }

  /**
   * Retourne true si l'utilisateur possède au moins l'un des rôles donnés.
   * Utilisation : `*ngIf="sbvpAuth.hasAnyRole(['admin_sbvp','admin_application']) | async"`
   */
  hasAnyRole(roles: SbvpRole[]): Observable<boolean> {
    return this.sbvpRole$.pipe(map(current => current !== null && roles.includes(current)));
  }

  /**
   * Retourne true si l'utilisateur peut accéder à l'application donnée.
   * - admin_sbvp → toujours true
   * - autres → vérifie allowedApplicationIds
   */
  canAccessApplication(applicationId: string): Observable<boolean> {
    return combineLatest([
      this.store.select(isAdminSbvp),
      this.store.select(getAllowedApplicationIds),
    ]).pipe(
      map(([isAdmin, allowedIds]) => {
        if (isAdmin) return true;
        if (allowedIds === null) return true;
        return allowedIds.includes(applicationId);
      }),
    );
  }

  /**
   * Retourne true si l'utilisateur peut accéder au client donné.
   * - admin_sbvp → toujours true
   * - autres → vérifie allowedClientIds
   */
  canAccessClient(clientId: string): Observable<boolean> {
    return combineLatest([
      this.store.select(isAdminSbvp),
      this.store.select(getAllowedClientIds),
    ]).pipe(
      map(([isAdmin, allowedIds]) => {
        if (isAdmin) return true;
        if (allowedIds === null) return true;
        return allowedIds.includes(clientId);
      }),
    );
  }

  /**
   * Retourne la liste des IDs d'applications autorisées.
   * null = accès total (admin_sbvp), [] = aucun accès.
   */
  getAllowedApplicationIds(): Observable<string[] | null> {
    return this.store.select(getAllowedApplicationIds);
  }

  /**
   * Retourne la liste des IDs de clients autorisés.
   * null = accès total (admin_sbvp), [] = aucun accès.
   */
  getAllowedClientIds(): Observable<string[] | null> {
    return this.store.select(getAllowedClientIds);
  }

  /**
   * Filtre une liste d'éléments en ne conservant que ceux dont l'id est autorisé.
   * Si allowedApplicationIds est null (admin_sbvp), retourne tous les éléments.
   */
  filterAllowedApplications<T extends { id: string }>(items: T[]): Observable<T[]> {
    return this.getAllowedApplicationIds().pipe(
      map(allowedIds =>
        allowedIds === null ? items : items.filter(i => allowedIds.includes(i.id)),
      ),
    );
  }

  /**
   * Filtre une liste de clients en ne conservant que ceux dont l'id est autorisé.
   * Si allowedClientIds est null (admin_sbvp), retourne tous les éléments.
   */
  filterAllowedClients<T extends { id: string }>(items: T[]): Observable<T[]> {
    return this.getAllowedClientIds().pipe(
      map(allowedIds =>
        allowedIds === null ? items : items.filter(i => allowedIds.includes(i.id)),
      ),
    );
  }
}
